import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  Settings as SettingsIcon, 
  Bell, 
  Shield, 
  Eye, 
  Moon, 
  Sun, 
  Globe, 
  Volume2,
  Mail,
  Smartphone,
  Lock,
  Key,
  UserCheck,
  Save,
  RotateCcw
} from 'lucide-react';
import BackButton from '../components/BackButton';

export default function Settings() {
  const { isLoggedIn } = useAuth();
  
  // 设置状态
  const [settings, setSettings] = useState({
    // 通知设置
    notifications: {
      email: true,
      push: false,
      sms: false,
      priceAlerts: true,
      tradingUpdates: true,
      marketNews: false
    },
    // 隐私设置
    privacy: {
      profileVisibility: 'public',
      activityVisibility: 'friends',
      walletVisibility: 'private'
    },
    // 安全设置
    security: {
      twoFactorAuth: false,
      loginNotifications: true,
      sessionTimeout: '30'
    },
    // 界面设置
    interface: {
      theme: 'dark',
      language: 'en',
      currency: 'USD',
      soundEffects: true
    }
  });

  const [hasChanges, setHasChanges] = useState(false);

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-plypto-bg flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Access Denied</h1>
          <p className="text-gray-400">Please connect your wallet to access settings.</p>
        </div>
      </div>
    );
  }

  const handleSettingChange = (category, key, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: value
      }
    }));
    setHasChanges(true);
  };

  const handleSave = () => {
    // TODO: 这里可以添加API调用来保存设置
    console.log('保存设置:', settings);
    setHasChanges(false);
    // 显示保存成功提示
  };

  const handleReset = () => {
    // 重置为默认设置
    setSettings({
      notifications: {
        email: true,
        push: false,
        sms: false,
        priceAlerts: true,
        tradingUpdates: true,
        marketNews: false
      },
      privacy: {
        profileVisibility: 'public',
        activityVisibility: 'friends',
        walletVisibility: 'private'
      },
      security: {
        twoFactorAuth: false,
        loginNotifications: true,
        sessionTimeout: '30'
      },
      interface: {
        theme: 'dark',
        language: 'en',
        currency: 'USD',
        soundEffects: true
      }
    });
    setHasChanges(true);
  };

  return (
    <div className="bg-plypto-bg min-h-screen">
      {/* Settings Header */}
      <div className="max-w-7xl mx-auto px-6 pt-6 pb-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <BackButton />
            <div className="h-6 w-px bg-white/20"></div>
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-gray-500 to-gray-700 rounded-full flex items-center justify-center">
                <SettingsIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Settings</h1>
                <p className="text-gray-400">Manage your account preferences and security</p>
              </div>
            </div>
          </div>
          
          {hasChanges && (
            <div className="flex items-center space-x-3">
              <button
                onClick={handleReset}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg transition-colors text-white"
              >
                <RotateCcw className="w-4 h-4" />
                <span>Reset</span>
              </button>
              <button
                onClick={handleSave}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors text-white"
              >
                <Save className="w-4 h-4" />
                <span>Save Changes</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Settings Content */}
      <div className="max-w-7xl mx-auto px-6 pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          {/* Notification Settings */}
          <div className="bg-white/5 rounded-xl p-6 border border-white/10">
            <div className="flex items-center space-x-3 mb-6">
              <Bell className="w-6 h-6 text-blue-400" />
              <h2 className="text-xl font-semibold text-white">Notification Preferences</h2>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-gray-400" />
                  <div>
                    <div className="text-white font-medium">Email Notifications</div>
                    <div className="text-gray-400 text-sm">Receive updates via email</div>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.notifications.email}
                    onChange={(e) => handleSettingChange('notifications', 'email', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Smartphone className="w-5 h-5 text-gray-400" />
                  <div>
                    <div className="text-white font-medium">Push Notifications</div>
                    <div className="text-gray-400 text-sm">Browser push notifications</div>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.notifications.push}
                    onChange={(e) => handleSettingChange('notifications', 'push', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Bell className="w-5 h-5 text-gray-400" />
                  <div>
                    <div className="text-white font-medium">Price Alerts</div>
                    <div className="text-gray-400 text-sm">Get notified of price changes</div>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.notifications.priceAlerts}
                    onChange={(e) => handleSettingChange('notifications', 'priceAlerts', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
          </div>

          {/* Privacy Settings */}
          <div className="bg-white/5 rounded-xl p-6 border border-white/10">
            <div className="flex items-center space-x-3 mb-6">
              <Eye className="w-6 h-6 text-green-400" />
              <h2 className="text-xl font-semibold text-white">Privacy Settings</h2>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Profile Visibility
                </label>
                <select
                  value={settings.privacy.profileVisibility}
                  onChange={(e) => handleSettingChange('privacy', 'profileVisibility', e.target.value)}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500"
                >
                  <option value="public" className="bg-gray-800">Public</option>
                  <option value="friends" className="bg-gray-800">Friends Only</option>
                  <option value="private" className="bg-gray-800">Private</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Activity Visibility
                </label>
                <select
                  value={settings.privacy.activityVisibility}
                  onChange={(e) => handleSettingChange('privacy', 'activityVisibility', e.target.value)}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500"
                >
                  <option value="public" className="bg-gray-800">Public</option>
                  <option value="friends" className="bg-gray-800">Friends Only</option>
                  <option value="private" className="bg-gray-800">Private</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Wallet Visibility
                </label>
                <select
                  value={settings.privacy.walletVisibility}
                  onChange={(e) => handleSettingChange('privacy', 'walletVisibility', e.target.value)}
                  className="w-full px-3 py-2 pr-10 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500"
                >
                  <option value="public" className="bg-gray-800">Public</option>
                  <option value="friends" className="bg-gray-800">Friends Only</option>
                  <option value="private" className="bg-gray-800">Private</option>
                </select>
              </div>
            </div>
          </div>

          {/* Security Settings */}
          <div className="bg-white/5 rounded-xl p-6 border border-white/10">
            <div className="flex items-center space-x-3 mb-6">
              <Shield className="w-6 h-6 text-red-400" />
              <h2 className="text-xl font-semibold text-white">Security Options</h2>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Key className="w-5 h-5 text-gray-400" />
                  <div>
                    <div className="text-white font-medium">Two-Factor Authentication</div>
                    <div className="text-gray-400 text-sm">Add extra security to your account</div>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.security.twoFactorAuth}
                    onChange={(e) => handleSettingChange('security', 'twoFactorAuth', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                </label>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Lock className="w-5 h-5 text-gray-400" />
                  <div>
                    <div className="text-white font-medium">Login Notifications</div>
                    <div className="text-gray-400 text-sm">Get notified of new logins</div>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.security.loginNotifications}
                    onChange={(e) => handleSettingChange('security', 'loginNotifications', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                </label>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Session Timeout (minutes)
                </label>
                <select
                  value={settings.security.sessionTimeout}
                  onChange={(e) => handleSettingChange('security', 'sessionTimeout', e.target.value)}
                  className="w-full px-3 py-2 pr-10 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500"
                >
                  <option value="15" className="bg-gray-800">15 minutes</option>
                  <option value="30" className="bg-gray-800">30 minutes</option>
                  <option value="60" className="bg-gray-800">1 hour</option>
                  <option value="120" className="bg-gray-800">2 hours</option>
                </select>
              </div>
            </div>
          </div>

          {/* Interface Settings */}
          <div className="bg-white/5 rounded-xl p-6 border border-white/10">
            <div className="flex items-center space-x-3 mb-6">
              <Moon className="w-6 h-6 text-purple-400" />
              <h2 className="text-xl font-semibold text-white">Interface Settings</h2>
            </div>
            
            <div className="space-y-4">
              {/* <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Theme
                </label>
                <select
                  value={settings.interface.theme}
                  onChange={(e) => handleSettingChange('interface', 'theme', e.target.value)}
                  className="w-full px-3 py-2 pr-10 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                >
                  <option value="dark" className="bg-gray-800">Dark</option>
                  <option value="light" className="bg-gray-800">Light</option>
                  <option value="auto" className="bg-gray-800">Auto</option>
                </select>
              </div> */}
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Language
                </label>
                <select
                  value={settings.interface.language}
                  onChange={(e) => handleSettingChange('interface', 'language', e.target.value)}
                  className="w-full px-3 py-2 pr-10 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                >
                  <option value="en" className="bg-gray-800">English</option>
                  <option value="zh" className="bg-gray-800">中文</option>
                  <option value="ja" className="bg-gray-800">日本語</option>
                  <option value="ko" className="bg-gray-800">한국어</option>
                </select>
              </div>
              
              {/* <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Currency
                </label>
                <select
                  value={settings.interface.currency}
                  onChange={(e) => handleSettingChange('interface', 'currency', e.target.value)}
                  className="w-full px-3 py-2 pr-10 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                >
                  <option value="USD" className="bg-gray-800">USD</option>
                  <option value="EUR" className="bg-gray-800">EUR</option>
                  <option value="CNY" className="bg-gray-800">CNY</option>
                  <option value="JPY" className="bg-gray-800">JPY</option>
                </select>
              </div> */}
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Volume2 className="w-5 h-5 text-gray-400" />
                  <div>
                    <div className="text-white font-medium">Sound Effects</div>
                    <div className="text-gray-400 text-sm">Enable interface sounds</div>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.interface.soundEffects}
                    onChange={(e) => handleSettingChange('interface', 'soundEffects', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
